import React, { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom'
import HomePage from './pages/HomePage'
import AppPage from './pages/AppPage'
import MembershipPage from './pages/MembershipPage'
import AppLandingPage from './pages/AppLandingPage'
import LampDemoPage from './pages/LampDemo'
import SidebarDemoPage from './pages/SidebarDemo'
import MarqueeDemoPage from './pages/MarqueeDemo'
import CarouselDemoPage from './pages/CarouselDemo'
import ExpertiseDemoPage from './pages/ExpertiseDemo'
import ServicesGalleryDemoPage from './pages/ServicesGalleryDemo'
import HeroCtaDemoPage from './pages/HeroCtaDemo'
import NewsletterDemoPage from './pages/NewsletterDemo'
import CursorWanderCardDemoPage from './pages/CursorWanderCardDemo'
import NavigationMenuDemoPage from './pages/NavigationMenuDemo'
import ImageGalleryDemoPage from './pages/ImageGalleryDemo'
import HudsonDarkCardDemoPage from './pages/HudsonDarkCardDemo'
import AboutPage from './pages/AboutPage'
import HomeInsightsPage from './pages/HomeInsightsPage'
import FAQsPage from './pages/FAQsPage'
import ServicesPage from './pages/ServicesPage'
import ProductPage from './pages/ProductPage'
import OctoberOfferPage from './pages/OctoberOfferPage'
import IowaHomeownersLuxury from './pages/blog/IowaHomeownersLuxury'
import ProactiveMaintenanceGuide from './pages/blog/ProactiveMaintenanceGuide'
import PricingSliderDemoPage from './pages/PricingSliderDemo'
import WhoIsHudsonPage from './pages/WhoIsHudsonPage'
import StyleGuidePage from './pages/StyleGuidePage'
import QuizFunnelPage from './pages/QuizFunnelPage'
import PartnersPage from './pages/PartnersPage'
import BuildersPage from './pages/BuildersPage'
import PricingPage from './pages/PricingPage'
import FranchisePage from './pages/FranchisePage'
import PitchDeckPage from './pages/PitchDeckPage'
import BrandAssetsPage from './pages/BrandAssetsPage'
import SmartStartPage from './pages/SmartStartPage'
import MyHomeRefreshPage from './pages/MyHomeRefreshPage'
import ReferPage from './pages/ReferPage'
import SchemaMarkup from './components/SchemaMarkup'
import { SmoothScroll } from './components/SharedLayout'
import { FunnelProvider } from './contexts/FunnelContext'
import { AuthProvider } from './contexts/AuthContext'
import { AuthGuard, AdminGuard } from './components/portal/AuthGuard'
import Step1Page from './pages/funnel/Step1Page'
import Step2Page from './pages/funnel/Step2Page'
import Step3Page from './pages/funnel/Step3Page'
import Step4Page from './pages/funnel/Step4Page'
import CheckoutPage from './pages/funnel/CheckoutPage'
import ThankYouPage from './pages/funnel/ThankYouPage'
import RefreshConfirmedOfferPage from './pages/funnel/RefreshConfirmedOfferPage'
import FoundingMemberCheckoutPage from './pages/funnel/FoundingMemberCheckoutPage'
import WelcomeMemberPage from './pages/funnel/WelcomeMemberPage'
import FoundingMemberPage from './pages/FoundingMemberPage'
import NeighborhoodPage from './pages/NeighborhoodPage'
import SnapshotPage from './pages/SnapshotPage'

import PortalLayout from './components/portal/PortalLayout'
import PortalErrorBoundary from './components/portal/ErrorBoundary'

const PortalLoginPage = lazy(() => import('./pages/portal/LoginPage'))
const PortalSignupPage = lazy(() => import('./pages/portal/SignupPage'))
const DashboardPage = lazy(() => import('./pages/portal/DashboardPage'))
const ScorePage = lazy(() => import('./pages/portal/ScorePage'))
const ScoreResultPage = lazy(() => import('./pages/portal/ScoreResultPage'))
const PortalSnapshotPage = lazy(() => import('./pages/portal/SnapshotPage'))
const BlueprintPage = lazy(() => import('./pages/portal/BlueprintPage'))
const VaultPage = lazy(() => import('./pages/portal/VaultPage'))
const VaultRecordPage = lazy(() => import('./pages/portal/VaultRecordPage'))
const RequestsPage = lazy(() => import('./pages/portal/RequestsPage'))
const RequestDetailPage = lazy(() => import('./pages/portal/RequestDetailPage'))
const SchedulePage = lazy(() => import('./pages/portal/SchedulePage'))
const PropertyLookupPage = lazy(() => import('./pages/portal/PropertyLookupPage'))
const PropertyReportPage = lazy(() => import('./pages/portal/PropertyReportPage'))
const PortalMembershipPage = lazy(() => import('./pages/portal/MembershipPage'))
const AdminDashboardPage = lazy(() => import('./pages/portal/AdminDashboardPage'))
const AdminUserPage = lazy(() => import('./pages/portal/AdminUserPage'))

function ScrollToTop() {
  const location = useLocation()
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '')
      const el = document.getElementById(id)
      if (el) {
        const t = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300)
        return () => clearTimeout(t)
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [location.pathname, location.hash])
  return null
}

const PortalSpinner = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#0D3135] border-t-transparent" />
  </div>
)

function App() {
  return (
    <AuthProvider>
      <FunnelProvider>
        <Router>
          <ScrollToTop />
          <SmoothScroll />
          <SchemaMarkup type="organization" />
          <SchemaMarkup type="localBusiness" />
          <SchemaMarkup type="service" />
          <SchemaMarkup type="website" />
          <SchemaMarkup type="faqPage" />
          <div className="min-h-0 bg-gray-50">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/app" element={<AppPage />} />
              <Route path="/membership" element={<MembershipPage />} />
              <Route path="/app-landing" element={<AppLandingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/home-insights" element={<HomeInsightsPage />} />
              <Route path="/who-is-hudson" element={<WhoIsHudsonPage />} />
              <Route path="/styles" element={<StyleGuidePage />} />
              <Route path="/quiz" element={<QuizFunnelPage />} />
              {/* Membership application form removed — redirect old /apply links to /membership */}
              <Route path="/apply" element={<Navigate to="/membership" replace />} />
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/builders" element={<BuildersPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/franchise" element={<FranchisePage />} />
              <Route path="/pitch-deck" element={<PitchDeckPage />} />
              <Route path="/brand-assets" element={<BrandAssetsPage />} />
              <Route path="/smartstart" element={<SmartStartPage />} />
              <Route path="/smart-start" element={<SmartStartPage />} />
              {/* Common mistaken URLs (no React route = blank page) */}
              <Route path="/services/smart-start" element={<Navigate to="/smart-start" replace />} />
              <Route path="/services/smartstart" element={<Navigate to="/smart-start" replace />} />
              <Route path="/refresh" element={<MyHomeRefreshPage />} />
              <Route path="/myhome-refresh" element={<MyHomeRefreshPage />} />
              <Route path="/refer" element={<ReferPage />} />
              <Route path="/snapshot" element={<SnapshotPage />} />
              <Route path="/faqs" element={<FAQsPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/product" element={<ProductPage />} />
              <Route path="/october-offer" element={<OctoberOfferPage />} />
              <Route path="/blog/iowa-luxury" element={<IowaHomeownersLuxury />} />
              <Route path="/blog/proactive-maintenance" element={<ProactiveMaintenanceGuide />} />
              <Route path="/lamp-demo" element={<LampDemoPage />} />
              <Route path="/sidebar-demo" element={<SidebarDemoPage />} />
              <Route path="/marquee-demo" element={<MarqueeDemoPage />} />
              <Route path="/carousel-demo" element={<CarouselDemoPage />} />
              <Route path="/expertise-demo" element={<ExpertiseDemoPage />} />
              <Route path="/services-gallery-demo" element={<ServicesGalleryDemoPage />} />
              <Route path="/hero-cta-demo" element={<HeroCtaDemoPage />} />
              <Route path="/newsletter-demo" element={<NewsletterDemoPage />} />
              <Route path="/cursor-wander-card-demo" element={<CursorWanderCardDemoPage />} />
              <Route path="/navigation-menu-demo" element={<NavigationMenuDemoPage />} />
              <Route path="/image-gallery-demo" element={<ImageGalleryDemoPage />} />
              <Route path="/hudson-dark-card-demo" element={<HudsonDarkCardDemoPage />} />
              <Route path="/pricing-slider-demo" element={<PricingSliderDemoPage />} />
              {/* Winter Refresh Funnel Routes */}
              <Route path="/winter-refresh/step-1" element={<Step1Page />} />
              <Route path="/winter-refresh/step-2" element={<Step2Page />} />
              <Route path="/winter-refresh/step-3" element={<Step3Page />} />
              <Route path="/winter-refresh/step-4" element={<Step4Page />} />
              <Route path="/winter-refresh/checkout" element={<CheckoutPage />} />
              <Route path="/winter-refresh/thank-you" element={<ThankYouPage />} />
              {/* Founding Member Upsell Funnel Routes */}
              <Route path="/refresh-confirmed-offer" element={<RefreshConfirmedOfferPage />} />
              <Route path="/founding-member-checkout" element={<FoundingMemberCheckoutPage />} />
              <Route path="/welcome-member" element={<WelcomeMemberPage />} />
              <Route path="/founding" element={<FoundingMemberPage />} />

              {/* Portal auth (no guard) */}
              <Route path="/portal/login" element={<Suspense fallback={<PortalSpinner />}><PortalLoginPage /></Suspense>} />
              <Route path="/portal/signup" element={<Suspense fallback={<PortalSpinner />}><PortalSignupPage /></Suspense>} />

              {/* Portal (authenticated) */}
              <Route path="/portal" element={<AuthGuard><PortalErrorBoundary><Suspense fallback={<PortalSpinner />}><PortalLayout /></Suspense></PortalErrorBoundary></AuthGuard>}>
                <Route index element={<Suspense fallback={<PortalSpinner />}><DashboardPage /></Suspense>} />
                <Route path="score" element={<Suspense fallback={<PortalSpinner />}><ScorePage /></Suspense>} />
                <Route path="score/:id" element={<Suspense fallback={<PortalSpinner />}><ScoreResultPage /></Suspense>} />
                <Route path="snapshot/:id" element={<Suspense fallback={<PortalSpinner />}><ScoreResultPage /></Suspense>} />
                <Route path="snapshot" element={<Suspense fallback={<PortalSpinner />}><PortalSnapshotPage /></Suspense>} />
                <Route path="blueprint" element={<Suspense fallback={<PortalSpinner />}><BlueprintPage /></Suspense>} />
                <Route path="vault" element={<Suspense fallback={<PortalSpinner />}><VaultPage /></Suspense>} />
                <Route path="vault/:id" element={<Suspense fallback={<PortalSpinner />}><VaultRecordPage /></Suspense>} />
                <Route path="requests" element={<Suspense fallback={<PortalSpinner />}><RequestsPage /></Suspense>} />
                <Route path="requests/:id" element={<Suspense fallback={<PortalSpinner />}><RequestDetailPage /></Suspense>} />
                <Route path="schedule" element={<Suspense fallback={<PortalSpinner />}><SchedulePage /></Suspense>} />
                <Route path="property" element={<Suspense fallback={<PortalSpinner />}><PropertyLookupPage /></Suspense>} />
                <Route path="property/:id" element={<Suspense fallback={<PortalSpinner />}><PropertyReportPage /></Suspense>} />
                <Route path="membership" element={<Suspense fallback={<PortalSpinner />}><PortalMembershipPage /></Suspense>} />
                <Route path="admin" element={<AdminGuard><Suspense fallback={<PortalSpinner />}><AdminDashboardPage /></Suspense></AdminGuard>} />
                <Route path="admin/users/:id" element={<AdminGuard><Suspense fallback={<PortalSpinner />}><AdminUserPage /></Suspense></AdminGuard>} />
              </Route>

              {/* Neighborhood landings: /ankeny, /west-des-moines, /prairie-trail; unknown slugs use metro fallback in page */}
              <Route path="/:neighborhoodSlug" element={<NeighborhoodPage />} />
            </Routes>
          </div>
        </Router>
      </FunnelProvider>
    </AuthProvider>
  )
}

export default App 